// Spec 28 PR 1 — ProgressCard.
//
// Live status for ingest jobs. Signature element: per-row status
// indicator that pulses subtly while running, holds steady when
// done. No icons used as state markers — pure typography +
// minimal motion. Each row subscribes to the project's jobs SSE
// (existing primitive) and updates locally; the parent surface
// gets the `all_complete` action when every job hits a terminal
// success state.

import { useEffect, useRef, useState } from "react";

import type { WidgetRenderProps } from "../registry";

interface ProgressItem {
	id: string;
	label: string;
	jobId: string;
}

interface ProgressCardProps {
	title: string;
	projectId: string;
	items: ProgressItem[];
	onAllCompleteAction: string;
}

type JobStatus = "pending" | "running" | "succeeded" | "failed";

interface JobEventPayload {
	status?: JobStatus;
	progress?: number;
	message?: string;
}

export function ProgressCard({
	props,
	state,
	onAction,
}: WidgetRenderProps<ProgressCardProps>) {
	const [statuses, setStatuses] = useState<Record<string, JobStatus>>(
		Object.fromEntries(props.items.map((i) => [i.jobId, "pending"])),
	);
	const firedRef = useRef(false);

	const handleStatus = (jobId: string, status: JobStatus) => {
		setStatuses((prev) =>
			prev[jobId] === status ? prev : { ...prev, [jobId]: status },
		);
	};

	useEffect(() => {
		if (firedRef.current) return;
		const allDone = props.items.every(
			(it) => statuses[it.jobId] === "succeeded",
		);
		if (allDone) {
			firedRef.current = true;
			onAction(props.onAllCompleteAction, {
				totalJobs: props.items.length,
			});
		}
	}, [statuses, props.items, props.onAllCompleteAction, onAction]);

	if (state.kind === "succeeded") {
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					{props.items.length}{" "}
					{props.items.length === 1 ? "ingest" : "ingests"} complete
				</span>
			</div>
		);
	}

	return (
		<div className="py-8 space-y-5">
			<div className="text-center text-[20px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
				{props.title}
			</div>

			<div className="max-w-[480px] mx-auto space-y-1.5">
				{props.items.map((item, i) => (
					<ProgressRow
						key={item.id}
						label={item.label}
						jobId={item.jobId}
						projectId={props.projectId}
						status={statuses[item.jobId] ?? "pending"}
						onStatus={(s) => handleStatus(item.jobId, s)}
						delayMs={i * 60}
					/>
				))}
			</div>
		</div>
	);
}

function ProgressRow({
	label,
	jobId,
	projectId,
	status,
	onStatus,
	delayMs,
}: {
	label: string;
	jobId: string;
	projectId: string;
	status: JobStatus;
	onStatus: (s: JobStatus) => void;
	delayMs: number;
}) {
	// Subscribe to the existing jobs SSE channel. The endpoint
	// streams { status, progress, message } payloads we already
	// emit from the worker; we read `status` and drop the rest.
	useEffect(() => {
		const url = `/api/projects/${projectId}/jobs/${jobId}/events`;
		const es = new EventSource(url, { withCredentials: true });
		const handler = (ev: MessageEvent) => {
			try {
				const data = JSON.parse(ev.data) as JobEventPayload;
				if (data.status) onStatus(data.status);
			} catch {
				/* ignore malformed frame */
			}
		};
		es.onmessage = handler;
		es.onerror = () => {
			// On error, the connection auto-retries; nothing for us to
			// do. The row will keep its last known status.
		};
		return () => es.close();
	}, [projectId, jobId, onStatus]);

	const statusLabel =
		status === "pending"
			? "queued"
			: status === "running"
				? "indexing"
				: status === "succeeded"
					? "done"
					: "failed";

	const statusColor =
		status === "succeeded"
			? "var(--color-success)"
			: status === "failed"
				? "var(--color-danger)"
				: status === "running"
					? "rgba(167,139,250,0.85)"
					: "var(--color-text-dim)";

	return (
		<div
			className="flex items-center gap-3 px-3 h-9 rounded-md bg-white/[0.02] border border-[var(--color-border)] anim-fade-rise"
			style={{ animationDelay: `${delayMs}ms` }}
		>
			<span
				className="block w-1.5 h-1.5 rounded-full shrink-0"
				style={{
					background: statusColor,
					animation:
						status === "running" ? "skeleton-pulse 1.4s ease-in-out infinite" : "none",
				}}
			/>
			<span className="text-[12px] mono flex-1 truncate text-[var(--color-text)]">
				{label}
			</span>
			<span
				className="text-[10px] uppercase tracking-wider"
				style={{ color: statusColor }}
			>
				{statusLabel}
			</span>
		</div>
	);
}
